// Workspace controller: the local folder's lifecycle (SPEC §4, §10 states).
// Plain class + subscribe, so it's testable without React.
import type { GitHub } from '../github/client';
import { gitBlobShaOfText } from './blobSha';
import { lineStats, type LineStats } from './changes';
import { basePath, Folder, FolderGoneError, FolderPermissionError, pickFolder, recallFolder, rememberFolder, forgetFolder, type PickResult } from './folder';
import { requestWriteLock, type WriteLock } from './lock';
import { pull, type PullProgress, type PullResult } from './pull';
import { checkFolder, emptyState, readState, writeState, type WorkState } from './state';

export type FileStatus = {
  path: string;
  status: 'pushed' | 'edited' | 'new' | 'deleted';
  /** Changed lines against the last pulled version (edited and new files). */
  stats?: LineStats;
};

/** What we know about a file's content, keyed by size + mtime so unchanged files aren't re-read. */
type ScanEntry = { size: number; lastModified: number; sha: string; stats: LineStats | undefined };

export type RefusedReason = 'not-empty' | 'other-repo' | 'damaged' | 'cancelled';

export type WorkspacePhase =
  | { kind: 'none'; refused?: { reason: RefusedReason; detail?: string } }
  | { kind: 'checking' }
  | { kind: 'reconnect'; folderName: string }
  | { kind: 'gone' }
  | { kind: 'locked' }
  | { kind: 'downloading'; folderName: string; progress: PullProgress; first: boolean }
  | { kind: 'ready'; folderName: string; state: WorkState; files: FileStatus[]; lastPull?: PullResult; pulling?: boolean }
  | { kind: 'error'; message: string };

export type WorkspaceDeps = {
  gh: GitHub;
  repo: { owner: string; name: string; full: string };
  pick?: () => Promise<PickResult>;
  recall?: () => Promise<FileSystemDirectoryHandle | undefined>;
  remember?: (h: FileSystemDirectoryHandle) => Promise<void>;
  forget?: () => Promise<void>;
  lock?: (steal: boolean) => WriteLock;
};

export class Workspace {
  #phase: WorkspacePhase = { kind: 'checking' };
  #listeners = new Set<() => void>();
  #folder: Folder | null = null;
  #lock: WriteLock | null = null;
  #scan = new Map<string, ScanEntry>();
  readonly #d: Required<WorkspaceDeps>;

  constructor(deps: WorkspaceDeps) {
    this.#d = {
      pick: pickFolder, recall: recallFolder, remember: rememberFolder, forget: forgetFolder,
      lock: (steal) => requestWriteLock({ steal }),
      ...deps,
    };
  }

  get phase(): WorkspacePhase { return this.#phase; }
  subscribe = (fn: () => void) => { this.#listeners.add(fn); return () => this.#listeners.delete(fn); };
  #set(p: WorkspacePhase) { this.#phase = p; this.#listeners.forEach((l) => l()); }

  /** Map folder errors to the right screen; everything else is an error message. */
  async #guard(fn: () => Promise<void>): Promise<void> {
    try { await fn(); } catch (e) {
      if (e instanceof FolderGoneError) return this.#set({ kind: 'gone' });
      if (e instanceof FolderPermissionError && this.#folder) return this.#set({ kind: 'reconnect', folderName: this.#folder.name });
      this.#set({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
    }
  }

  /** On launch: reopen the remembered folder, if any. */
  start(): Promise<void> {
    return this.#guard(async () => {
      this.#set({ kind: 'checking' });
      const handle = await this.#d.recall();
      if (!handle) return this.#set({ kind: 'none' });
      this.#folder = new Folder(handle);
      if ((await this.#folder.permission()) !== 'granted') return this.#set({ kind: 'reconnect', folderName: this.#folder.name });
      await this.#open(false);
    });
  }

  /** "Reconnect your folder" banner button (needs a click). */
  reconnect(): Promise<void> {
    return this.#guard(async () => {
      if (!this.#folder) return this.#set({ kind: 'none' });
      if (!(await this.#folder.requestPermission())) return this.#set({ kind: 'reconnect', folderName: this.#folder.name });
      await this.#open(false);
    });
  }

  /** "Use this window": take the lock from another window. */
  useThisWindow(): Promise<void> {
    return this.#guard(() => this.#open(true));
  }

  /** "Choose folder" (setup step 3, or Change folder). */
  choose(): Promise<void> {
    return this.#guard(async () => {
      const picked = await this.#d.pick();
      if (picked.kind === 'cancelled') return this.#set({ kind: 'none', refused: { reason: 'cancelled' } });
      const folder = new Folder(picked.handle);
      const check = await checkFolder(folder, this.#d.repo.full);
      if (check.kind === 'not-empty') return this.#set({ kind: 'none', refused: { reason: 'not-empty' } });
      if (check.kind === 'other-repo') return this.#set({ kind: 'none', refused: { reason: 'other-repo', detail: check.repo } });
      if (check.kind === 'damaged') return this.#set({ kind: 'none', refused: { reason: 'damaged', detail: check.reason } });
      this.#release();
      this.#folder = folder;
      await this.#d.remember(picked.handle);
      await this.#open(false);
    });
  }

  /** Avatar menu "Change folder": forget this one (its files stay on disk). */
  async changeFolder(): Promise<void> {
    this.#release();
    this.#folder = null;
    await this.#d.forget();
    this.#set({ kind: 'none' });
  }

  /** "Pull" (git pull origin <branch>). */
  pull(): Promise<void> {
    return this.#guard(async () => {
      const p = this.#phase;
      if (p.kind !== 'ready' || !this.#folder) return;
      this.#set({ ...p, pulling: true });
      const { result, state } = await pull({ gh: this.#d.gh, folder: this.#folder, repo: this.#d.repo, state: p.state });
      this.#scan.clear(); // bases changed
      this.#set({ kind: 'ready', folderName: this.#folder.name, state, files: await this.#statuses(state), lastPull: result });
    });
  }

  /** Re-scan for edits made here or in other editors (SPEC §4 change detection). */
  refresh(): Promise<void> {
    return this.#guard(async () => {
      const p = this.#phase;
      if (p.kind !== 'ready' || !this.#folder || p.pulling) return;
      const files = await this.#statuses(p.state);
      const cur = this.#phase;
      if (cur.kind === 'ready' && !cur.pulling && JSON.stringify(files) !== JSON.stringify(cur.files)) this.#set({ ...cur, files });
    });
  }

  dispose() { this.#release(); }

  // ---------- internals ----------

  #release() { this.#lock?.release(); this.#lock = null; this.#scan.clear(); }

  async #open(steal: boolean) {
    const folder = this.#folder!;
    this.#release();
    const lock = this.#d.lock(steal);
    if (!(await lock.acquired)) return this.#set({ kind: 'locked' });
    this.#lock = lock;
    lock.onLost(() => { this.#lock = null; this.#set({ kind: 'locked' }); });

    let state = await readState(folder);
    const first = !state;
    if (!state) {
      state = emptyState(this.#d.repo.full);
      await writeState(folder, state);
    }
    if (first || !state.remoteHeadSha) {
      // First download into this folder (or a download that was interrupted).
      this.#set({ kind: 'downloading', folderName: folder.name, progress: { done: 0, total: 0 }, first: true });
      const r = await pull({
        gh: this.#d.gh, folder, repo: this.#d.repo, state,
        onProgress: (progress) => this.#set({ kind: 'downloading', folderName: folder.name, progress, first: true }),
      });
      return this.#set({ kind: 'ready', folderName: folder.name, state: r.state, files: await this.#statuses(r.state), lastPull: r.result });
    }
    this.#set({ kind: 'ready', folderName: folder.name, state, files: await this.#statuses(state) });
  }

  /**
   * Status of every file. Compares size and mtime first and only reads and hashes files
   * that changed since the last scan (approved before M3).
   */
  async #statuses(state: WorkState): Promise<FileStatus[]> {
    const folder = this.#folder!;
    const stats = await folder.statTracked();
    const onDisk = new Map(stats.map((s) => [s.path, s]));
    for (const path of this.#scan.keys()) if (!onDisk.has(path)) this.#scan.delete(path);

    const out: FileStatus[] = [];
    for (const path of new Set([...onDisk.keys(), ...Object.keys(state.files)])) {
      const entry = state.files[path];
      const st = onDisk.get(path);
      if (!st) { out.push({ path, status: 'deleted' }); continue; }
      let known = this.#scan.get(path);
      if (!known || known.size !== st.size || known.lastModified !== st.lastModified) {
        const text = (await folder.readText(path)) ?? '';
        const sha = await gitBlobShaOfText(text);
        const edited = !entry || sha !== entry.localSha;
        const base = edited ? ((await folder.readText(basePath(path))) ?? '') : '';
        known = { size: st.size, lastModified: st.lastModified, sha, stats: edited ? lineStats(base, text) : undefined };
        this.#scan.set(path, known);
      }
      if (!entry) out.push({ path, status: 'new', ...(known.stats ? { stats: known.stats } : {}) });
      else if (known.sha === entry.localSha) out.push({ path, status: 'pushed' });
      else out.push({ path, status: 'edited', ...(known.stats ? { stats: known.stats } : {}) });
    }
    return out.sort((a, b) => a.path.localeCompare(b.path));
  }
}
